// EmptyState — centered icon + title + body + optional action.
// Mirrors the `.empty` family in /Users/harry/Downloads/employee-portal/project/styles.css
// (lines 1654–1668). Used across home.jsx ("No active access yet", "Nothing
// frequent yet", "Your queue is clear", etc.).
//
// Layout: flex column, centered, gap 8px, padding 32px 16px, text-align center.
// Icon: 40×40 rounded square (radius 10px / `component.xl`) with neutral.100
// background and neutral.500 foreground; inner glyph rendered at ~20px.
// Title: 14px/600 neutral.900. Body: 13px regular neutral.700, max-width 320px.

import { Box, Text } from "@chakra-ui/react";
import { ReactElement, ReactNode, cloneElement, isValidElement } from "react";
import { TbInbox } from "react-icons/tb";

export interface EmptyStateProps {
  /** Optional icon element. Defaults to TbInbox. Rendered at ~20px inside a
   *  40×40 rounded tile. */
  icon?: ReactElement;
  /** Heading text (14px/600). */
  title: string;
  /** Optional body copy (13px neutral.700, max-width 320). */
  body?: string;
  /** Optional CTA rendered below the body. */
  action?: ReactNode;
}

const EmptyState = ({
  icon = <TbInbox />,
  title,
  body,
  action,
}: EmptyStateProps) => {
  // Inject the canonical 20px size on whatever icon is supplied unless the
  // caller already set a fontSize/size prop.
  const sizedIcon = isValidElement(icon)
    ? cloneElement(icon as ReactElement<{ size?: number | string }>, {
        size: (icon.props as { size?: number | string }).size ?? 20,
      })
    : icon;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      px="lg"
      py="2xl"
      gap="sm"
    >
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        width="40px"
        height="40px"
        borderRadius="component.xl"
        bg="neutral.100"
        color="neutral.500"
        fontSize="20px"
      >
        {sizedIcon}
      </Box>
      <Text fontSize="md" fontWeight={600} color="neutral.900" lineHeight={1.3}>
        {title}
      </Text>
      {body && (
        <Text
          fontSize="smd"
          color="neutral.700"
          maxW="320px"
          lineHeight={1.5}
        >
          {body}
        </Text>
      )}
      {action && <Box mt="2xs">{action}</Box>}
    </Box>
  );
};

EmptyState.displayName = "EmptyState";

export default EmptyState;
